<script setup lang="ts">
import type { MemorySummary } from '@/api/memory/memoryType';
import { Icon } from '@iconify/vue';

defineProps<{
  summary: MemorySummary | null;
  loading: boolean;
}>();

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('zh-CN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}
</script>

<template>
  <section class="border-b pb-6">
    <div class="mb-4 flex items-start justify-between gap-4">
      <div>
        <h3 class="flex items-center gap-2 text-base font-semibold">
          <Icon icon="lucide:brain-circuit" class="size-5 text-primary" />
          AI 学习记忆
        </h3>
        <p class="mt-1 text-sm text-muted-foreground">
          根据你的平台活动自动整理，用于帮助 AI 助教理解学习背景。
        </p>
      </div>
      <div v-if="summary" class="shrink-0 text-right text-xs text-muted-foreground">
        <div>{{ summary.model_used || 'AI 摘要' }}</div>
        <div class="mt-1">
          {{ formatDateTime(summary.updated_at) }}
        </div>
      </div>
    </div>

    <div v-if="loading" class="space-y-3 animate-pulse">
      <div class="h-4 w-5/6 rounded bg-muted" />
      <div class="h-4 w-full rounded bg-muted" />
      <div class="h-4 w-3/5 rounded bg-muted" />
    </div>

    <div v-else-if="summary" class="space-y-4">
      <p class="whitespace-pre-wrap text-sm leading-7 text-foreground/90">
        {{ summary.summary }}
      </p>
      <div class="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
        <span class="inline-flex items-center gap-1.5">
          <Icon icon="lucide:database" class="size-3.5" />
          基于 {{ summary.event_count }} 条活动记录
        </span>
        <span class="inline-flex items-center gap-1.5">
          <Icon icon="lucide:clock-3" class="size-3.5" />
          最近更新：{{ formatDateTime(summary.updated_at) }}
        </span>
      </div>
    </div>

    <div v-else class="flex items-start gap-3 rounded-md border border-dashed px-4 py-5 text-sm text-muted-foreground">
      <Icon icon="lucide:brain" class="mt-0.5 size-5 shrink-0" />
      <p>暂时还没有 AI 学习摘要。继续使用平台后，系统会自动生成并更新你的学习背景。</p>
    </div>
  </section>
</template>
